import { Link } from '@tanstack/react-router'

import { useMyPurchases, type MemberPurchase } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { Button, PhotoCover, Pill, cn } from '@lb/ui'

import { http } from '../../lib/client'
import { useSessionContext } from '../../app/providers/session'
import { formatDateShort, formatDayOfMonth, formatMonthShort, formatRelativeTime } from '../../lib/format'

/**
 * « Vos achats » — what somebody owns outright, on their own Home.
 *
 * The design gives a single-purchase member this strip directly under the
 * greeting, and under it one sentence about what they do not have: *« You own
 * 1 of 20+ courses »*. Both numbers come from the same read, together, because
 * that sentence is about two numbers and a client holding one of them
 * eventually renders the other wrongly.
 *
 * ── Read only for somebody who might own something ───────────────────────
 * An entitled member's purchases change nothing they can see: the library is
 * already open to them, and the strip's whole job is to say *these are yours
 * and the rest is not*. So the read is skipped for them rather than fired on
 * every Home load to render nothing.
 *
 * A member who bought a course and later subscribed loses the strip, which is
 * right — there is nothing left to distinguish.
 *
 * ── No rendered sentences ────────────────────────────────────────────────
 * « Accès à vie · Pas commencée » and « Dans 8 jours » are assembled here from
 * `accessEndsAt`, the progress counts and `event.startsAt`. The API sends the
 * facts, in the member's language only where a title needs translating.
 */
export function PurchasesStrip() {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const entitled = viewer?.entitlement === 'entitled'
  const purchases = useMyPurchases(http, !entitled)

  const data = purchases.data
  if (!data || data.items.length === 0) return null

  const courses = data.items.filter((item) => item.contentType === 'COURSE').length

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-serif text-[22px] text-ink">{t('home.yourPurchases')}</h2>

      <ul className="grid gap-gap-card sm:grid-cols-2">
        {data.items.map((item) => (
          <li key={item.contentItemId}>
            <PurchaseCard item={item} />
          </li>
        ))}
      </ul>

      {/*
       * The membership line, and the one number that makes it concrete. A
       * purchase is never credited toward a subscription, which the plans page
       * says where somebody is deciding — this is only the invitation.
       */}
      <div className="flex flex-col gap-3 rounded-[var(--r-lg)] bg-ink p-card-lg text-cream sm:flex-row sm:items-center sm:justify-between">
        <div>
          {/*
           * Counted here from the items, and NOT from `owned` — which is every
           * purchase, tickets included, while `totalCourses` is courses. The
           * pair was meant to travel together and does not: one course and one
           * ticket read « You own 2 of 146 courses ». `items` is the whole set
           * (there is no paging on this read), so counting it is exact.
           * Reported in request 21.
           */}
          <p className="font-serif text-[20px]">
            {t('home.ownedOfTotal', { owned: courses, total: data.totalCourses })}
          </p>
          <p className="mt-1 max-w-[62ch] text-[13px] text-cream/80">{t('home.ownedBody')}</p>
        </div>
        <Button asChild variant="secondary" className="flex-none">
          <Link to="/plans">{t('home.seeMembership')}</Link>
        </Button>
      </div>
    </section>
  )
}

export function PurchaseCard({ item }: { item: MemberPurchase }) {
  const { t, i18n } = useTranslation()
  const event = item.contentType === 'EVENT'
  const progress = item.progress

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
      {/*
       * The cover, as every other card on Home draws it (Asana 16 Oct 2026:
       * « When a single user purchases a training, we don't show the cover
       * photo »). `GET /me/purchases` has always sent `coverUrl`; the card just
       * never read it. A course's square artwork whole on its navy ground, an
       * event's photo filling the frame — the « Pour vous » rail's own rule.
       * `PhotoCover` draws its placeholder when an item has no cover.
       */}
      <PhotoCover
        src={item.coverUrl}
        seed={item.contentItemId}
        ratio="16/10"
        fit={event ? 'cover' : 'contain'}
        tone={event ? 'amande' : 'navy'}
      />
      <div
        className={cn(
          'flex items-start gap-3 p-card',
          /* Bleu gris for a ticket, navy for a course — two neutrals, so the
           * pair reads as two kinds of thing without spending an accent on
           * decoration (ACCENTS.md). The design's pink band is rose, which is
           * the error surface here. */
          event ? 'bg-bleu-gris' : 'bg-ink text-cream',
        )}
      >
        {event && item.event ? (
          <span className="flex-none rounded-[var(--r-sm)] bg-surface px-2.5 py-1 text-center">
            {/* The platform's timezone, never the device's: a 19:00 Montreal
              * event is already tomorrow in UTC, and `new Date().getDate()`
              * printed « 21 » over an event on the 20th. */}
            <span className="block font-num text-[18px] font-semibold leading-none text-ink">
              {formatDayOfMonth(item.event.startsAt)}
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">
              {formatMonthShort(item.event.startsAt)}
            </span>
          </span>
        ) : null}
        <div className="min-w-0">
          <Pill tone={event ? 'navy' : 'amande'} size="sm">
            {event ? t('home.badgeTicket') : t('home.badgeOwned')}
          </Pill>
          {/*
           * `text-ink-on-tint`, not `text-ink`: bleu gris is a FIXED tint that
           * does not flip with the theme, and `--ink` does — so the title went
           * from navy on grey to near-invisible pale-on-pale the moment the
           * dark theme was on. The pinned token is the rule for text on any
           * tint (ACCENTS.md), and this is what it is for.
           */}
          <p
            className={cn(
              'mt-2 text-[15px] font-semibold',
              event ? 'text-ink-on-tint' : 'text-cream',
            )}
          >
            {item.title}
          </p>
        </div>
      </div>

      <div className="flex flex-1 items-end justify-between gap-3 p-card">
        <p className="text-[12.5px] text-muted">
          {event
            ? t('home.joinLater')
            : /*
               * « Accès à vie » is `accessEndsAt === null`, which is every
               * course and is the server's answer rather than ours. The
               * progress half is the resume state the strip would otherwise
               * need a second request for.
               */
              [
                item.accessEndsAt === null
                  ? t('home.lifetime')
                  : t('home.accessUntil', { date: formatDateShort(item.accessEndsAt) }),
                progress && progress.modulesComplete > 0
                  ? t('home.modulesDone', {
                      done: progress.modulesComplete,
                      total: progress.modulesTotal,
                    })
                  : t('home.notStarted'),
              ].join(' · ')}
        </p>
        <Link
          to={event ? '/events' : '/resources/courses/$id'}
          {...(event ? {} : { params: { id: item.contentItemId } })}
          className="flex-none text-[13px] font-semibold text-ink underline underline-offset-2"
        >
          {event ? t('home.ticketDetails') : t('home.startNow')}
        </Link>
      </div>

      {event && item.event ? (
        <p className="px-card pb-card text-[12px] text-muted">
          {formatRelativeTime(item.event.startsAt, i18n.language)}
        </p>
      ) : null}
    </article>
  )
}
